/**
 * Projects Hook
 *
 * Manages project data and operations
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { recordDealStageChanged } from '../lib/analytics/productEvents';
import { isDashboardTeaserStub } from '../lib/projectFilters';
import { applyLifecycleChange, applyStageChange } from '../lib/dealFunnel';

/**
 * Guest emails from public domains granted corbelcap.com domain access.
 * Maps specific email addresses to their effective domain.
 */
const EMAIL_TO_DOMAIN_OVERRIDE: Record<string, string> = {
  'adel.lotfi.benslimane@gmail.com': 'corbelcap.com',
};

const INITIAL_SNAPSHOT_FALLBACK_MS = 6000;
const logProjectDebug = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    console.log(...args);
  }
};

const warnProjectDebug = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    console.warn(...args);
  }
};

/**
 * Get the effective domain for an email address.
 * Returns the override domain for guest emails, otherwise the actual email domain.
 */
export const getEffectiveDomain = (email: string): string => {
  const normalizedEmail = email.toLowerCase();
  return EMAIL_TO_DOMAIN_OVERRIDE[normalizedEmail] || normalizedEmail.split('@')[1] || '';
};

/**
 * Get organization ID based on email domain
 * @param email User's email address
 * @returns Organization ID for the user's domain
 */
export const getOrganizationIdFromEmail = (email: string): string => {
  const effectiveDomain = getEffectiveDomain(email);

  // Map domains to organization IDs
  // CRITICAL: @corbelcap.com users should be in 'corbel-capital' organization
  // NOTE: We own returncatalyst.ai, NOT returncatalyst.com
  const domainToOrg: Record<string, string> = {
    'corbelcap.com': 'corbel-capital',
    'corbelcap.ai': 'corbel-capital',
    'returncatalyst.ai': 'returncatalyst',
  };

  if (domainToOrg[effectiveDomain]) {
    return domainToOrg[effectiveDomain];
  }

  return effectiveDomain.split('.')[0] || 'default-org';
};

/**
 * Deal Lifecycle Status
 * Tracks the company's relationship status with the firm throughout the deal lifecycle.
 * This is DIFFERENT from `stage` which tracks the deal process (screening → diligence → IC → closed).
 *
 * - prospect: Initial lead/opportunity being evaluated
 * - active_deal: In active pursuit (signed NDA, LOI, etc.)
 * - portfolio_company: Successfully acquired, now owned/invested
 * - exited: Successfully sold/divested
 * - dead: Deal died at any stage (passed, lost, walked away)
 */
export type DealLifecycle = 'prospect' | 'active_deal' | 'portfolio_company' | 'exited' | 'dead';

/**
 * Deal Structure Type - classifies the investment type
 */
export type DealStructureType = 'platform' | 'add_on' | 'growth' | 'buyout' | 'carve_out' | 'recap' | 'other';

/**
 * Deal Lifecycle History Entry - tracks transitions for analytics
 */
export interface DealLifecycleHistoryEntry {
  from: DealLifecycle | null;
  to: DealLifecycle;
  changedBy: string;
  changedAt: Date;
  notes?: string;
}

export interface Project {
  id: string;
  name: string;
  companyName: string;
  industry: string;
  sector?: string;
  stage: 'screening' | 'diligence' | 'ic' | 'closed' | 'passed';
  team: string[];
  enterpriseValue?: number;
  revenue?: number;
  ebitda?: number;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  // NEW: Sector-specific AI background
  headerImageUrl?: string;
  headerImageSector?: string;
  // NEW: Deal Lifecycle Tracking
  // NEW: Domain-based access control
  visibleToDomains?: string[];
  dealContext?: string;  // Free-text deal context (e.g., "distressed situation, paying 4x EBITDA")
  dealLifecycle?: DealLifecycle;
  dealStructureType?: DealStructureType;
  dealLifecycleHistory?: DealLifecycleHistoryEntry[];
  metadata?: {
    dealSource?: string;
    targetCloseDate?: Date;
    notes?: string;
    // NEW: Exit details (populated when dealLifecycle is 'exited')
    exitDate?: Date;
    exitMultiple?: number; // MOIC
    exitIRR?: number;
    exitNotes?: string;
    // NEW: Death details (populated when dealLifecycle is 'dead')
    deadDate?: Date;
    deadReason?: string;
  };
}

export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, userProfile } = useAuth();

  // Pinned projects from user preferences
  const pinnedProjects: string[] = (userProfile?.preferences?.pinnedProjects as string[]) || [];

  useEffect(() => {
    if (!user) {
      setProjects([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // 1. Query for projects where user is in the team
    const teamQuery = query(
      collection(db, 'projects'),
      where('team', 'array-contains', user.uid)
    );

    // 2. Query for projects visible to user's effective domain
    // Uses getEffectiveDomain() to support guest email overrides (e.g., personal Gmail → corbelcap.com).
    // Domain bridging is handled at the data layer: all projects store both
    // bridged domains (e.g., ['corbelcap.com', 'corbelcap.ai']) in visibleToDomains,
    // so querying for the user's own domain finds all bridged projects.
    let domainQuery: any = null;
    if (user.email) {
      const domain = getEffectiveDomain(user.email);
      if (domain) {
        domainQuery = query(
          collection(db, 'projects'),
          where('visibleToDomains', 'array-contains', domain)
        );
      }
    }

    // Log domain query details for debugging
    if (user.email) {
      const domain = getEffectiveDomain(user.email);
      logProjectDebug('[useProjects] Domain sharing debug:', {
        userEmail: user.email,
        effectiveDomain: domain,
        hasDomainQuery: !!domainQuery,
      });
    }

    // Store results from both queries
    let teamProjects: Project[] = [];
    let domainProjects: Project[] = [];
    let domainUnsubscribe: () => void;
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
    let initialSnapshotResolved = false;

    // Safely convert Firestore timestamps, plain dates, strings, or numbers to Date
    const safeToDate = (val: any): Date => {
      if (!val) return new Date();
      if (typeof val.toDate === 'function') return val.toDate();
      if (val instanceof Date) return val;
      if (typeof val === 'number') return new Date(val);
      if (typeof val === 'string') return new Date(val);
      if (val.seconds != null) return new Date(val.seconds * 1000); // Firestore Timestamp-like object
      if (val._seconds != null) return new Date(val._seconds * 1000); // JSON.stringify'd Timestamp
      return new Date();
    };

    // Helper to merge and update state
    const updateMergedProjects = () => {
      initialSnapshotResolved = true;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = undefined;
      }

      const projectMap = new Map<string, Project>();

      // Add team projects
      teamProjects.forEach(p => projectMap.set(p.id, p));

      // Add domain projects (overwriting duplicates, which is fine as they are the same)
      domainProjects.forEach(p => projectMap.set(p.id, p));

      const merged = Array.from(projectMap.values()).filter((p) => !isDashboardTeaserStub(p));

      // Sort client-side by createdAt descending
      merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      logProjectDebug('[useProjects] Merged results:', {
        teamCount: teamProjects.length,
        domainCount: domainProjects.length,
        mergedTotal: merged.length,
        uniqueFromDomain: merged.length - teamProjects.length,
      });

      setProjects(merged);
      setLoading(false);
    };

    const runInitialFetchFallback = async () => {
      if (initialSnapshotResolved) return;

      warnProjectDebug('[useProjects] Initial onSnapshot did not resolve; falling back to getDocs');
      try {
        const [teamSnapshot, domainSnapshot] = await Promise.all([
          getDocs(teamQuery),
          domainQuery ? getDocs(domainQuery) : Promise.resolve(null),
        ]);

        if (initialSnapshotResolved) return;

        teamProjects = teamSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: safeToDate(data.createdAt),
            updatedAt: safeToDate(data.updatedAt),
            metadata: data.metadata ? {
              ...data.metadata,
              targetCloseDate: data.metadata.targetCloseDate ? safeToDate(data.metadata.targetCloseDate) : undefined,
            } : undefined,
          } as Project;
        });

        domainProjects = domainSnapshot?.docs.map((doc: any) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: safeToDate(data.createdAt),
            updatedAt: safeToDate(data.updatedAt),
            metadata: data.metadata ? {
              ...data.metadata,
              targetCloseDate: data.metadata.targetCloseDate ? safeToDate(data.metadata.targetCloseDate) : undefined,
            } : undefined,
          } as Project;
        }) || [];

        updateMergedProjects();
      } catch (err: any) {
        if (initialSnapshotResolved) return;
        console.error('[useProjects] Initial fetch fallback failed:', err);
        setError(err?.message || 'Failed to load projects');
        setLoading(false);
      }
    };

    fallbackTimer = setTimeout(runInitialFetchFallback, INITIAL_SNAPSHOT_FALLBACK_MS);

    // Subscribe to team query
    const teamUnsubscribe = onSnapshot(
      teamQuery,
      (snapshot) => {
        logProjectDebug('[useProjects] Team query returned:', snapshot.docs.length, 'projects');
        teamProjects = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: safeToDate(data.createdAt),
            updatedAt: safeToDate(data.updatedAt),
            metadata: data.metadata ? {
              ...data.metadata,
              targetCloseDate: data.metadata.targetCloseDate ? safeToDate(data.metadata.targetCloseDate) : undefined,
            } : undefined,
          } as Project;
        });
        updateMergedProjects();
      },
      (err) => {
        console.error('Error fetching team projects:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    // Subscribe to domain query if applicable
    if (domainQuery) {
      domainUnsubscribe = onSnapshot(
        domainQuery,
        (snapshot: any) => {
          logProjectDebug('[useProjects] Domain query returned:', snapshot.docs.length, 'projects');
          if (snapshot.docs.length > 0) {
            logProjectDebug('[useProjects] Domain project samples:', snapshot.docs.slice(0, 3).map((d: any) => ({
              id: d.id,
              name: d.data().name || d.data().companyName,
              visibleToDomains: d.data().visibleToDomains,
            })));
          }
          domainProjects = snapshot.docs.map((doc: any) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: safeToDate(data.createdAt),
              updatedAt: safeToDate(data.updatedAt),
              metadata: data.metadata ? {
                ...data.metadata,
                targetCloseDate: data.metadata.targetCloseDate ? safeToDate(data.metadata.targetCloseDate) : undefined,
              } : undefined,
            } as Project;
          });
          updateMergedProjects();
        },
        (err: any) => {
          console.error('[useProjects] DOMAIN QUERY FAILED:', err.code, err.message, err);
          // Surface this error so it's not silently hidden
          if (err.code === 'permission-denied') {
            console.error(
              '[useProjects] PERMISSION DENIED on domain query. ' +
              'This means Firestore security rules are blocking the domain-based project query. ' +
              'Check that the allow list rule supports domain queries independently.'
            );
          }
        }
      );
    }

    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (teamUnsubscribe) teamUnsubscribe();
      if (domainUnsubscribe) domainUnsubscribe();
    };
  }, [user?.uid, user?.email]);

  const createProject = async (projectData: Omit<Project, 'id' | 'createdBy' | 'createdAt' | 'updatedAt' | 'team'>) => {
    if (!user) throw new Error('User must be authenticated');

    try {
      await user.getIdToken(true);

      const createProjectFn = httpsCallable<
        {
          name: string;
          companyName: string;
          industry: string;
          stage: Project['stage'];
          enterpriseValue?: number;
          revenue?: number;
          ebitda?: number;
          metadata?: Project['metadata'];
        },
        { success: boolean; projectId: string; message?: string }
      >(functions, 'createProject', { timeout: 120_000 });

      const payload: {
        name: string;
        companyName: string;
        industry: string;
        stage: Project['stage'];
        enterpriseValue?: number;
        revenue?: number;
        ebitda?: number;
        metadata?: Project['metadata'];
      } = {
        name: projectData.name,
        companyName: projectData.companyName,
        industry: projectData.industry,
        stage: projectData.stage,
      };

      // Only add optional numeric fields if they have valid values
      if (projectData.enterpriseValue !== undefined && !isNaN(projectData.enterpriseValue)) {
        payload.enterpriseValue = projectData.enterpriseValue;
      }
      if (projectData.revenue !== undefined && !isNaN(projectData.revenue)) {
        payload.revenue = projectData.revenue;
      }
      if (projectData.ebitda !== undefined && !isNaN(projectData.ebitda)) {
        payload.ebitda = projectData.ebitda;
      }
      if (projectData.metadata) {
        payload.metadata = projectData.metadata;
      }

      const result = await createProjectFn(payload);
      if (!result.data?.projectId) {
        throw new Error(result.data?.message || 'Failed to create project');
      }
      return result.data.projectId;
    } catch (err: any) {
      console.error('Error creating project:', err);
      throw new Error(err.message || 'Failed to create project');
    }
  };

  const updateProject = async (projectId: string, updates: Partial<Project>) => {
    try {
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      console.error('Error updating project:', err);
      throw new Error(err.message || 'Failed to update project');
    }
  };

  const deleteProject = async (projectId: string) => {
    try {
      const projectRef = doc(db, 'projects', projectId);
      await deleteDoc(projectRef);
    } catch (err: any) {
      console.error('Error deleting project:', err);
      throw new Error(err.message || 'Failed to delete project');
    }
  };

  /**
   * Update deal lifecycle with history tracking
   * This is the primary way to change a deal's lifecycle status
   */
  const updateDealLifecycle = async (
    projectId: string,
    newLifecycle: DealLifecycle,
    notes?: string,
    additionalMetadata?: Partial<Project['metadata']>
  ) => {
    if (!user) throw new Error('User must be authenticated');

    try {
      const projectRef = doc(db, 'projects', projectId);
      const project = projects.find(p => p.id === projectId);

      if (!project) {
        throw new Error('Project not found');
      }

      const currentLifecycle = project.dealLifecycle || null;

      // Create history entry - only include notes if defined (Firestore rejects undefined)
      const historyEntry: DealLifecycleHistoryEntry = {
        from: currentLifecycle,
        to: newLifecycle,
        changedBy: user.uid,
        changedAt: new Date(),
        ...(notes !== undefined && notes !== null && { notes }),
      };

      // Build update object. The process stage ratchets FORWARD to stay
      // consistent with the lifecycle (canonical funnel — lib/dealFunnel.ts);
      // otherwise backend stage-scoping would silently lag the toggle.
      const resolved = applyLifecycleChange(newLifecycle, project.stage);
      const updates: any = {
        dealLifecycle: newLifecycle,
        updatedAt: serverTimestamp(),
      };
      if (resolved.stage !== project.stage) {
        updates.stage = resolved.stage;
      }

      // Append to history array (or create new)
      const existingHistory = project.dealLifecycleHistory || [];
      updates.dealLifecycleHistory = [...existingHistory, historyEntry];

      // Add additional metadata if provided (e.g., exit details)
      // Filter out undefined values since Firestore rejects them
      if (additionalMetadata) {
        const filteredMetadata = Object.fromEntries(
          Object.entries({ ...project.metadata, ...additionalMetadata })
            .filter(([_, value]) => value !== undefined)
        );
        if (Object.keys(filteredMetadata).length > 0) {
          updates.metadata = filteredMetadata;
        }
      }

      await updateDoc(projectRef, updates);

      recordDealStageChanged({
        projectId,
        fromStage: currentLifecycle,
        toStage: newLifecycle,
      });

      logProjectDebug(`Deal lifecycle updated: ${projectId} from ${currentLifecycle} to ${newLifecycle}`);
    } catch (err: any) {
      console.error('Error updating deal lifecycle:', err);
      throw new Error(err.message || 'Failed to update deal lifecycle');
    }
  };

  /**
   * Update the canonical process stage (project.stage) and roll the
   * dealLifecycle forward with it (lib/dealFunnel.ts). This is the field
   * backend stage-scoping reads, so advancing it right-sizes IC memo /
   * committee / DDQ generation.
   */
  const updateDealStage = async (projectId: string, newStage: Project['stage']) => {
    if (!user) throw new Error('User must be authenticated');

    try {
      const projectRef = doc(db, 'projects', projectId);
      const project = projects.find(p => p.id === projectId);

      if (!project) {
        throw new Error('Project not found');
      }
      if (project.stage === newStage) {
        return;
      }

      const resolved = applyStageChange(newStage, project.dealLifecycle);
      const updates: any = {
        stage: resolved.stage,
        updatedAt: serverTimestamp(),
      };

      // Lifecycle is a derived rollup — record the transition in history
      // like the manual toggle does.
      const currentLifecycle = project.dealLifecycle || null;
      if (resolved.dealLifecycle !== currentLifecycle) {
        const historyEntry: DealLifecycleHistoryEntry = {
          from: currentLifecycle,
          to: resolved.dealLifecycle,
          changedBy: user.uid,
          changedAt: new Date(),
          notes: `Derived from stage change to ${newStage}`,
        };
        updates.dealLifecycle = resolved.dealLifecycle;
        updates.dealLifecycleHistory = [...(project.dealLifecycleHistory || []), historyEntry];
      }

      await updateDoc(projectRef, updates);

      recordDealStageChanged({
        projectId,
        fromStage: project.stage,
        toStage: newStage,
      });

      logProjectDebug(`Deal stage updated: ${projectId} from ${project.stage} to ${newStage}`);
    } catch (err: any) {
      console.error('Error updating deal stage:', err);
      throw new Error(err.message || 'Failed to update deal stage');
    }
  };

  /**
   * Update deal structure type (platform, add-on, buyout, etc.)
   */
  const updateDealStructureType = async (
    projectId: string,
    structureType: DealStructureType
  ) => {
    try {
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        dealStructureType: structureType,
        updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      console.error('Error updating deal structure type:', err);
      throw new Error(err.message || 'Failed to update deal structure type');
    }
  };

  /**
   * Toggle pin/unpin a project for the current user
   */
  const togglePinProject = async (projectId: string) => {
    if (!user) throw new Error('User must be authenticated');

    try {
      const userRef = doc(db, 'users', user.uid);
      const isPinned = pinnedProjects.includes(projectId);
      await updateDoc(userRef, {
        'preferences.pinnedProjects': isPinned
          ? arrayRemove(projectId)
          : arrayUnion(projectId),
      });
    } catch (err: any) {
      console.error('Error toggling pin:', err);
      throw new Error(err.message || 'Failed to toggle pin');
    }
  };

  return {
    projects,
    loading,
    error,
    pinnedProjects,
    createProject,
    updateProject,
    deleteProject,
    updateDealLifecycle,
    updateDealStage,
    updateDealStructureType,
    togglePinProject,
  };
};
